import { useEffect } from "react";
import { X, Clock, Loader2 } from "lucide-react";

export default function RecipeModal({ open, onClose, name, recipe, loading, error, kcal }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-[rgba(42,39,36,0.55)] backdrop-blur-sm" onClick={onClose} data-testid="recipe-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-[0_30px_60px_-20px_rgba(42,39,36,0.4)]">
        <header className="px-6 pt-5 pb-4 border-b border-[color:var(--line-soft)] flex items-start justify-between gap-3">
          <div>
            <p className="overline">Recipe</p>
            <h3 className="font-serif text-2xl text-[color:var(--ink)] leading-snug">{name}</h3>
            {kcal != null && <p className="text-xs text-[color:var(--ink-3)] mt-1">{Math.round(kcal)} kcal</p>}
          </div>
          <button onClick={onClose} aria-label="Close" className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] transition-colors p-1" data-testid="recipe-close">
            <X size={18} />
          </button>
        </header>
        <div className="px-6 py-5 overflow-auto">
          {loading && (
            <div className="flex items-center gap-3 text-[color:var(--ink-2)] font-serif italic" data-testid="recipe-loading">
              <Loader2 size={16} className="animate-spin text-[color:var(--accent)]" /> Sopfie is writing the recipe…
            </div>
          )}
          {error && <p className="text-sm text-[color:var(--accent-2)]" data-testid="recipe-error">{error}</p>}
          {recipe && !loading && (
            <div data-testid="recipe-content">
              <div className="flex flex-wrap gap-4 text-xs text-[color:var(--ink-3)] mb-5">
                {recipe.prep_minutes != null && <span className="flex items-center gap-1.5"><Clock size={12} /> Prep {recipe.prep_minutes} min</span>}
                {recipe.cook_minutes != null && <span className="flex items-center gap-1.5"><Clock size={12} /> Cook {recipe.cook_minutes} min</span>}
                {recipe.servings != null && <span>Serves {recipe.servings}</span>}
              </div>
              {recipe.ingredients?.length > 0 && (
                <div className="mb-6">
                  <p className="overline mb-2">Ingredients</p>
                  <ul className="space-y-1.5 text-sm text-[color:var(--ink)]">
                    {recipe.ingredients.map((it, i) => (
                      <li key={i} className="flex gap-2"><span className="text-[color:var(--accent)] mt-1.5 w-1 h-1 rounded-full bg-[color:var(--accent)] shrink-0" /><span>{it}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {recipe.steps?.length > 0 && (
                <div className="mb-6">
                  <p className="overline mb-2">Steps</p>
                  <ol className="space-y-3 text-sm text-[color:var(--ink)] leading-relaxed">
                    {recipe.steps.map((s, i) => (
                      <li key={i} className="flex gap-3"><span className="font-serif text-[color:var(--accent)] text-lg leading-none">{i + 1}.</span><span>{s}</span></li>
                    ))}
                  </ol>
                </div>
              )}
              {recipe.tips?.length > 0 && (
                <div className="mt-5 pt-5 border-t border-[color:var(--line-soft)]">
                  <p className="overline mb-2">Sopfie's notes</p>
                  <ul className="space-y-1.5 text-sm text-[color:var(--ink-2)] italic">
                    {recipe.tips.map((t, i) => <li key={i}>· {t}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
